import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  Activity,
  Box,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  LayoutDashboard,
  KeyRound,
  Menu,
  Moon,
  Network,
  Bell,
  Search,
  Server,
  Settings,
  Shield,
  Sun,
  Terminal,
  Users,
  X,
  LogOut,
} from 'lucide-react'
import { useSession } from '../../context/SessionContext'
import { useTheme } from '../../context/ThemeContext'
import { cn } from '../../lib/cn'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Badge } from '../ui/Badge'
import { useTenantId } from '../../context/SessionContext'
import { useResourceGroupsEnabled } from '../../hooks/useResourceGroupsEnabled'

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  end?: boolean
}

const userNav: NavItem[] = [
  { to: '/catalog', label: 'Resources', icon: <Box className="h-4 w-4" />, end: true },
  { to: '/native-access-setup', label: 'Native Access', icon: <Terminal className="h-4 w-4" /> },
]

const adminNav: NavItem[] = [
  { to: '/admin', label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" />, end: true },
  { to: '/admin/pools', label: 'Connector Pools', icon: <Network className="h-4 w-4" /> },
  { to: '/admin/connectors', label: 'Connectors', icon: <Server className="h-4 w-4" /> },
  { to: '/admin/resources', label: 'Resources', icon: <Box className="h-4 w-4" /> },
  { to: '/admin/policies', label: 'Policies', icon: <Shield className="h-4 w-4" /> },
  { to: '/admin/identity-provider', label: 'Identity Provider', icon: <KeyRound className="h-4 w-4" /> },
  { to: '/admin/directory', label: 'Users & Groups', icon: <Users className="h-4 w-4" /> },
  { to: '/admin/cli-settings', label: 'CLI Settings', icon: <Settings className="h-4 w-4" /> },
  { to: '/admin/ssh-networks', label: 'SSH Networks', icon: <Terminal className="h-4 w-4" /> },
  { to: '/admin/audit', label: 'Audit Log', icon: <Activity className="h-4 w-4" /> },
]

function NavSection({
  title,
  items,
  collapsed,
}: {
  title?: string
  items: NavItem[]
  collapsed: boolean
}) {
  return (
    <div className="space-y-1">
      {title && !collapsed && (
        <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted">
          {title}
        </p>
      )}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              collapsed && 'justify-center px-2',
              isActive
                ? 'bg-sidebar-active text-primary'
                : 'text-sidebar-foreground hover:bg-surface-muted hover:text-foreground',
            )
          }
        >
          {item.icon}
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      ))}
    </div>
  )
}

export default function AppShell({ mode }: { mode: 'user' | 'admin' }) {
  const { user } = useSession()
  const tenantId = useTenantId()
  const { enabled: resourceGroupsEnabled } = useResourceGroupsEnabled(tenantId)
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  useEffect(() => {
    setMobileOpen(false)
    setProfileOpen(false)
    setNotifOpen(false)
    setHelpOpen(false)
  }, [location.pathname])

  const isAdmin = Boolean(user?.isAdmin)
  const navItems = mode === 'admin'
    ? [
        ...adminNav.slice(0, 3),
        ...(resourceGroupsEnabled
          ? [
              { to: '/admin/tags', label: 'Tags', icon: <Shield className="h-4 w-4" /> },
              { to: '/admin/resource-groups', label: 'Resource groups', icon: <Box className="h-4 w-4" /> },
            ]
          : []),
        ...adminNav.slice(3),
      ]
    : userNav

  const sidebar = (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar transition-[width]',
        collapsed ? 'w-[64px]' : 'w-[240px]',
      )}
    >
      <div className={cn('flex h-14 items-center border-b border-sidebar-border px-4', collapsed && 'justify-center px-2')}>
        <Link to={mode === 'admin' ? '/admin' : '/'} className="flex items-center gap-2">
          <span className="text-lg font-bold tracking-tight text-primary">Idefy</span>
          {!collapsed && mode === 'admin' && (
            <Badge variant="primary" className="text-[10px]">Admin</Badge>
          )}
        </Link>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto p-3">
        <NavSection items={navItems} collapsed={collapsed} />
        {mode === 'user' && isAdmin && (
          <div className="border-t border-sidebar-border pt-4">
            <NavLink
              to="/admin"
              title={collapsed ? 'Admin' : undefined}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground hover:bg-surface-muted hover:text-foreground',
                collapsed && 'justify-center px-2',
              )}
            >
              <Shield className="h-4 w-4" />
              {!collapsed && <span>Admin</span>}
            </NavLink>
          </div>
        )}
        {mode === 'admin' && (
          <div className="border-t border-sidebar-border pt-4">
            <NavLink
              to="/catalog"
              title={collapsed ? 'User portal' : undefined}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground hover:bg-surface-muted hover:text-foreground',
                collapsed && 'justify-center px-2',
              )}
            >
              <Box className="h-4 w-4" />
              {!collapsed && <span>User portal</span>}
            </NavLink>
          </div>
        )}
      </nav>

      <div className="hidden border-t border-sidebar-border p-2 md:block">
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'sm'}
          className={cn('w-full', !collapsed && 'justify-start')}
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          {!collapsed && <span>Collapse</span>}
        </Button>
      </div>
    </aside>
  )

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <div className="hidden md:block">{sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 z-50 w-[240px]">{sidebar}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-surface px-4">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-4 w-4" />
          </Button>

          <div className="relative hidden max-w-md flex-1 sm:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <Input
              className="pl-9"
              placeholder="Search…"
              aria-label="Global search"
              readOnly
              onFocus={(e) => e.currentTarget.blur()}
              title="Search coming soon"
            />
          </div>

          <div className="ml-auto flex items-center gap-1">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Notifications"
                onClick={() => {
                  setNotifOpen((o) => !o)
                  setHelpOpen(false)
                  setProfileOpen(false)
                }}
              >
                <Bell className="h-4 w-4" />
              </Button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-72 rounded-lg border border-border bg-surface p-4 text-sm shadow-sm">
                  <p className="font-medium text-foreground">Notifications</p>
                  <p className="mt-1 text-muted">No notifications yet.</p>
                </div>
              )}
            </div>

            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Help"
                onClick={() => {
                  setHelpOpen((o) => !o)
                  setNotifOpen(false)
                  setProfileOpen(false)
                }}
              >
                <CircleHelp className="h-4 w-4" />
              </Button>
              {helpOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-lg border border-border bg-surface p-3 text-sm shadow-sm">
                  <p className="mb-2 font-medium text-foreground">Help</p>
                  <a className="block rounded-md px-2 py-1.5 text-muted hover:bg-surface-muted hover:text-foreground" href="/native-access-setup">
                    Native access setup
                  </a>
                  <p className="mt-2 px-2 text-xs text-muted">Keyboard: press Esc to close dialogs.</p>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className="max-w-[200px]"
                onClick={() => {
                  setProfileOpen((o) => !o)
                  setNotifOpen(false)
                  setHelpOpen(false)
                }}
              >
                <span className="truncate text-xs">{user?.email ?? '…'}</span>
              </Button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-surface py-1 text-sm shadow-sm">
                  <div className="border-b border-border px-3 py-2">
                    <p className="truncate font-medium text-foreground">{user?.email}</p>
                    {isAdmin && <p className="text-xs text-muted">Administrator</p>}
                  </div>
                  {isAdmin && mode === 'user' && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 px-3 py-2 text-foreground hover:bg-surface-muted"
                    >
                      <Shield className="h-4 w-4" /> Admin portal
                    </Link>
                  )}
                  {mode === 'admin' && (
                    <Link
                      to="/catalog"
                      className="flex items-center gap-2 px-3 py-2 text-foreground hover:bg-surface-muted"
                    >
                      <Box className="h-4 w-4" /> User portal
                    </Link>
                  )}
                  <a
                    href="/auth/logout"
                    className="flex items-center gap-2 px-3 py-2 text-foreground hover:bg-surface-muted"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </a>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>

      {mobileOpen && (
        <button
          type="button"
          className="fixed right-3 top-3 z-50 rounded-lg border border-border bg-surface p-2 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
